import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import TablePagination from "@mui/material/TablePagination";
import { useQuery } from "@apollo/client";
import { useStyles } from "./styles";
import {
  handleChangeRowsPerPage,
  handleCloseSnackbar,
  handleChangePage
} from "./helpers";
import { GET_ALL_EXAM_PAPERS_DELIVERY } from "utils/graphql/services/ai-evaluation/queries";
import { Box, Tooltip } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory } from "react-router-dom";
import FilterComponent from "shared/molecules/Filter";
import FilterInputs from "./filter/FilterInputs";
import autoGradeClient from "utils/graphql/autoGradeClient";
import { convertToIST } from "../common/Index";

type MuiAlertProps = {
  onClose: () => void;
  severity?: "error" | "info" | "success" | "warning" | undefined;
  children?: React.ReactNode;
};

const Alert = React.forwardRef<HTMLDivElement, MuiAlertProps>((props, ref) => {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export function PaperDeliveryList() {
  const classes = useStyles();
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFilter, setIsFilter] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [examPaperId, setExamPaperId] = useState("");
  const [collegeId, setCollegeId] = useState("");
  const [academicYearId, setAcademicYearId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [classId, setClassId] = useState("");
  const [boardId, setBoardId] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTill, setCreatedTill] = useState("");
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [updatedTill, setUpdatedTill] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    examPaperId: "",
    collegeId: "",
    academicYearId: "",
    batchId: "",
    classId: "",
    boardId: "",
    createdFrom: "",
    createdTill: "",
    updatedFrom: "",
    updatedTill: "",
  });

  const buildFilterVariables = (pageOffset = 0, pageLimit = rowsPerPage) => ({
    input: {
      limit: pageLimit,
      offset: pageOffset,
      examPaperId: appliedFilters.examPaperId
        ? parseInt(appliedFilters.examPaperId)
        : undefined,
      collegeIds: appliedFilters.collegeId
        ? [parseInt(appliedFilters.collegeId)]
        : undefined,
      academicYearIds: appliedFilters.academicYearId
        ? [parseInt(appliedFilters.academicYearId)]
        : undefined,
      batchIds: appliedFilters.batchId
        ? [parseInt(appliedFilters.batchId)]
        : undefined,
      classIds: appliedFilters.classId
        ? [parseInt(appliedFilters.classId)]
        : undefined,
      boardIds: appliedFilters.boardId
        ? [parseInt(appliedFilters.boardId)]
        : undefined,
      createdFrom: appliedFilters.createdFrom || undefined,
      createdTill: appliedFilters.createdTill || undefined,
      updatedFrom: appliedFilters.updatedFrom || undefined,
      updatedTill: appliedFilters.updatedTill || undefined,
    },
  });

  const handleApplyFilter = () => {
    setAppliedFilters({
      examPaperId,
      collegeId,
      academicYearId,
      batchId,
      classId,
      boardId,
      createdFrom,
      createdTill,
      updatedFrom,
      updatedTill,
    });
    setPage(0);
    setIsFilter(false);
  };

  const handleClearFilter = () => {
    setExamPaperId("");
    setCollegeId("");
    setAcademicYearId("");
    setBatchId("");
    setClassId("");
    setBoardId("");
    setCreatedFrom("");
    setCreatedTill("");
    setUpdatedFrom("");
    setUpdatedTill("");
    setAppliedFilters({
      examPaperId: "",
      collegeId: "",
      academicYearId: "",
      batchId: "",
      classId: "",
      boardId: "",
      createdFrom: "",
      createdTill: "",
      updatedFrom: "",
      updatedTill: "",
    });

    setPage(0);
    setIsFilter(false);
  };

  const {
    data,
    loading,
    refetch: gamesRefetch,
  } = useQuery(GET_ALL_EXAM_PAPERS_DELIVERY, {
    variables: buildFilterVariables(page * rowsPerPage, rowsPerPage),
    client: autoGradeClient,
  });

  useEffect(() => {
    gamesRefetch(buildFilterVariables(page * rowsPerPage, rowsPerPage));
  }, [appliedFilters, page, rowsPerPage, gamesRefetch]);

  const paperDeliveries =
    data?.getExamPaperDeliveries?.examPaperDeliveries || [];
  const totalCount = data?.getExamPaperDeliveries?.totalRecords ?? 0;

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12}>
        <Paper elevation={3} className={classes.paper}>
          <Box display="flex" alignItems="center" justifyContent="end">
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                history.push("/ai-evaluation/paper-delivery/create")
              }
            >
              Create Exam Paper Delivery
            </Button>
          </Box>
          <Grid item mt={2}>
            <Box display="flex" justifyContent="flex-end">
              <FilterComponent isFilter={isFilter} setIsFilter={setIsFilter}>
                <div>
                  <FilterInputs
                    examPaperId={examPaperId}
                    setExamPaperId={setExamPaperId}
                    collegeId={collegeId}
                    setCollegeId={setCollegeId}
                    academicYearId={academicYearId}
                    setAcademicYearId={setAcademicYearId}
                    batchId={batchId}
                    setBatchId={setBatchId}
                    classId={classId}
                    setClassId={setClassId}
                    boardId={boardId}
                    setBoardId={setBoardId}
                    createdFrom={createdFrom}
                    setCreatedFrom={setCreatedFrom}
                    createdTill={createdTill}
                    setCreatedTill={setCreatedTill}
                    updatedFrom={updatedFrom}
                    setUpdatedFrom={setUpdatedFrom}
                    updatedTill={updatedTill}
                    setUpdatedTill={setUpdatedTill}
                    handleApplyFilter={handleApplyFilter}
                    handleClearFilter={handleClearFilter}
                  />
                </div>
              </FilterComponent>
            </Box>
          </Grid>
        </Paper>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress size={50} />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table stickyHeader sx={{ minWidth: 1500 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      background: "#fff",
                      whiteSpace: "nowrap",
                      width: 80,
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 120 }}>
                    Exam Paper Id
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 120 }}>
                    College Id
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 200 }}>
                    College Name
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 120 }}>
                    Academic Year Id
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 200 }}>
                    Academic Year Name
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 120 }}>
                    Batch Id
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 150 }}>
                    Batch Name
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 120 }}>
                    Class Id
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 150 }}>
                    Class Name
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 120 }}>
                    Board Id
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 150 }}>
                    Board Name
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 100 }}>
                    Is Archived
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 120 }}>
                    Delivery Count
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 150 }}>
                    Created By
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 150 }}>
                    Updated By
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 180 }}>
                    Created At
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 180 }}>
                    Updated At
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      background: "#fff",
                      zIndex: 2,
                      whiteSpace: "nowrap",
                      width: 100,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paperDeliveries.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        background: "#fff",
                        zIndex: 1,
                      }}
                    >
                      {row.id}
                    </TableCell>

                    <TableCell>{row.examPaperId}</TableCell>

                    <TableCell>
                      <Tooltip
                        title={(row.collegeIds || []).join(", ") || "N/A"}
                      >
                        <span>
                          {row.collegeIds && row.collegeIds.length > 0
                            ? row.collegeIds[0]
                            : "N/A"}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={(row.collegeNames || []).join(", ") || "N/A"}
                      >
                        <span>
                          {row.collegeNames && row.collegeNames.length > 0
                            ? row.collegeNames[0]
                            : "N/A"}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={(row.academicYearIds || []).join(", ") || "N/A"}
                      >
                        <span>
                          {row.academicYearIds && row.academicYearIds.length > 0
                            ? row.academicYearIds[0]
                            : "N/A"}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={
                          (row.academicYearNames || []).join(", ") || "N/A"
                        }
                      >
                        <span>
                          {row.academicYearNames &&
                            row.academicYearNames.length > 0
                            ? row.academicYearNames[0]
                            : "N/A"}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={(row.batchIds || []).join(", ") || "N/A"}>
                        <span>
                          {row.batchIds && row.batchIds.length > 0
                            ? row.batchIds[0]
                            : "N/A"}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={(row.batchNames || []).join(", ") || "N/A"}
                      >
                        <span>
                          {row.batchNames && row.batchNames.length > 0
                            ? row.batchNames[0]
                            : "N/A"}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={(row.classIds || []).join(", ") || "N/A"}>
                        <span>
                          {row.classIds && row.classIds.length > 0
                            ? row.classIds[0]
                            : "N/A"}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={(row.classNames || []).join(", ") || "N/A"}
                      >
                        <span>
                          {row.classNames && row.classNames.length > 0
                            ? row.classNames[0]
                            : "N/A"}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={(row.boardIds || []).join(", ") || "N/A"}>
                        <span>
                          {row.boardIds && row.boardIds.length > 0
                            ? row.boardIds[0]
                            : "N/A"}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={(row.boardNames || []).join(", ") || "N/A"}
                      >
                        <span>
                          {row.boardNames && row.boardNames.length > 0
                            ? row.boardNames[0]
                            : "N/A"}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>{row.isArchieved ? "Yes" : "No"}</TableCell>
                    <TableCell>{row.deliveryExecutionCount}</TableCell>
                    <TableCell>{row.createdByName}</TableCell>
                    <TableCell>{row.updatedByName}</TableCell>
                    <TableCell>{convertToIST(row.createdAt)}</TableCell>
                    <TableCell>{convertToIST(row.updatedAt)}</TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        background: "#fff",
                        zIndex: 1,
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() =>
                          history.push(
                            `/ai-evaluation/paper-delivery/view/${row.id}`
                          )
                        }
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <div className={classes.paginationWrapper}>
          <TablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => {
              handleChangePage(e, newPage, setPage);
              gamesRefetch(
                buildFilterVariables(newPage * rowsPerPage, rowsPerPage)
              );
            }}
            onRowsPerPageChange={(e: any) => {
              const newRowsPerPage = +e.target.value;
              handleChangeRowsPerPage(e, setRowsPerPage, setPage);
              gamesRefetch(buildFilterVariables(0, newRowsPerPage));
            }}
            rowsPerPageOptions={[]}
            component="div"
          />
        </div>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => handleCloseSnackbar(setSnackbar, snackbar)}
      >
        <Alert
          onClose={() => handleCloseSnackbar(setSnackbar, snackbar)}
          severity={snackbar.severity as MuiAlertProps["severity"]}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default PaperDeliveryList;
